/**
 * Personal to-do hooks — V5-04.
 *
 * Every endpoint is `/me/todos`; there is no variant that takes a user id, so
 * there is no way for this client to ask for someone else's list.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';

export interface TodoView {
  _id: string;
  title: string;
  note?: string;
  dueDate?: string;
  priority?: 'low' | 'normal' | 'high';
  status: 'open' | 'done';
  doneAt?: string;
  order: string;
  convertedToTaskId?: string;
  createdAt: string;
}

export type TodoStatusFilter = 'open' | 'done' | 'all';

export const todosKey = (status: TodoStatusFilter) => ['todos', status] as const;

export function useTodos(status: TodoStatusFilter = 'open') {
  return useQuery({
    queryKey: todosKey(status),
    queryFn: async () => (await api.get<TodoView[]>(`/me/todos?status=${status}`)).data,
  });
}

/** Open to-dos due today or overdue — the sidebar badge. */
export function useTodoBadge() {
  return useQuery({
    queryKey: ['todos', 'badge'],
    queryFn: async () => (await api.get<{ dueOrOverdue: number }>('/me/todos/badge')).data,
    // Cheap, and wrong-looking the moment it is stale.
    refetchOnWindowFocus: true,
  });
}

function useTodoMutation<TArgs>(fn: (args: TArgs) => Promise<unknown>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['todos'] });
    },
  });
}

export function useCreateTodo() {
  return useTodoMutation(
    async (input: { title: string; dueDate?: string | null; priority?: string | null }) =>
      (await api.post<TodoView>('/me/todos', input)).data,
  );
}

export function useUpdateTodo() {
  return useTodoMutation(
    async ({ id, ...patch }: { id: string } & Record<string, unknown>) =>
      (await api.patch<TodoView>(`/me/todos/${id}`, patch)).data,
  );
}

export function useDeleteTodo() {
  return useTodoMutation(async (id: string) => (await api.delete(`/me/todos/${id}`)).data);
}

export function useRestoreTodo() {
  return useTodoMutation(
    async (id: string) => (await api.post<TodoView>(`/me/todos/${id}/restore`, {})).data,
  );
}

export function useConvertTodo() {
  return useTodoMutation(
    async ({ id, projectId }: { id: string; projectId: string }) =>
      (await api.post<{ taskId: string; taskKey: string }>(`/me/todos/${id}/convert`, { projectId }))
        .data,
  );
}
